import {
  Button,
  ChevronDownIcon,
  List,
  Loading,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import { useInView } from 'react-intersection-observer';

import { useProductReviewsFetcher } from '@jane/bloom/hooks/useProductReviewsFetcher';
import type {
  ProductReviewViewModel,
  ProductReviewsSortOptions,
} from '@jane/bloom/viewModels/productReviews';

import { ProductReview } from './productReview';
import styles from './productReviews.module.css';

type ProductReviewsProps = {
  initialReviews: ProductReviewViewModel[];
  productId: number;
  productSlug: string | undefined;
  reviewCount: number;
};

const productReviewsSortOptions: ProductReviewsSortOptions[] = [
  {
    label: 'Recent',
    value: 'recent',
  },
  {
    label: 'Rating',
    value: 'rating',
  },
  {
    label: 'Helpful',
    value: 'upvote',
  },
];

export function ProductReviews({
  initialReviews,
  productId,
  productSlug,
  reviewCount,
}: ProductReviewsProps) {
  const { ref: sentinelRef } = useInView({
    onChange: (inView) => {
      if (inView && canLoadMoreReviews) {
        handleLoadMore();
      }
    },
  });
  const {
    canLoadMoreReviews,
    currentSortOrder,
    displayableReviews,
    handleLoadMore,
    handleSort,
    isLoading,
    reviewPaginationSummary,
    upvote,
  } = useProductReviewsFetcher({
    initialReviews,
    productId,
    productSlug,
    reviewCount,
  });

  const sortOptionLabel = productReviewsSortOptions.find(
    ({ value }) => value === currentSortOrder
  )?.label;

  return (
    <div className={styles['pdpReviewsWrapper']} data-testid="pdp-reviews">
      {reviewCount ? (
        <div className={styles['pdpReviewsSortSelectorContainer']}>
          <div className={styles['pdpReviewsCount']}>
            <Typography variant="body-bold" color="text-main">
              {reviewPaginationSummary}
            </Typography>
          </div>

          <Popover
            label=""
            target={
              <Button
                label={`Sort: ${sortOptionLabel}`}
                variant="tertiary"
                endIcon={<ChevronDownIcon color="text-main" size="sm" />}
              />
            }
          >
            {({ closePopover }) => (
              <>
                {productReviewsSortOptions.map((option) => {
                  return (
                    <List.Item
                      ariaLabel={option.label}
                      ml={8}
                      mr={8}
                      key={option.value}
                      onClick={() => {
                        handleSort(option.value);
                        closePopover();
                      }}
                    >
                      <Typography>{option.label}</Typography>
                    </List.Item>
                  );
                })}
              </>
            )}
          </Popover>
        </div>
      ) : null}
      <hr className={styles['pdpReviewsActivitiesDividerMobile']} />

      {displayableReviews.map((review) => (
        <ProductReview
          key={`${review.objectID}`}
          productId={productId}
          productSlug={productSlug}
          review={review}
          upvote={upvote}
        />
      ))}

      {isLoading && (
        <div className={styles['loading']}>
          <Loading color="black" />
        </div>
      )}

      <div ref={sentinelRef} aria-hidden="true" />
    </div>
  );
}
